import { DownloadService } from "@/services/admin-services";
import { Download, DynamicDownload } from "@/services/admin-services/models";
import { Software } from "@/services/global-services/models";
import { useDataStore, useFilterStore, useLocaleStore } from "@/stores";
import useExportStore from "@/stores/useExportStore";
import { isStructuredExportAvailable } from "@/utils/VersionUtils";
import clsx from "clsx";
import { parseJSON } from "date-fns";
import { saveAs } from "file-saver";
import { orderBy } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { getStaticDownloads } from "./StaticContent";

const idsEntry: Software = {
    id: "00000000-0000-0000-0000-000000000000",
    name: "IDS",
    active: true,
    bimQLabel: "IDS",
    version: "DEFAULT",
    order: 999,
    icon: "",
    color: "",
    defaultActive: false,
    isIfc: false,
};

const Downloads = () => {
    const regions = useLocaleStore((state) => state.availableRegions);
    const activeRegion = useLocaleStore((state) => state.activeRegion);

    const versions = useDataStore((state) => state.versions);
    const disciplines = useDataStore((state) => state.disciplines);
    const software = useDataStore((state) => state.software);
    const useCases = useDataStore((state) => state.useCases);

    const activeVersion = useFilterStore((state) => state.activeVersion);
    const activeDisciplines = useFilterStore((state) => state.activeDisciplines);
    const activeSoftware = useFilterStore((state) => state.activeSoftware);
    const activeIfcVersion = useFilterStore((state) => state.activeIfcVersion);
    const activeUseCases = useFilterStore((state) => state.activeUseCases);

    const [downloads, setDownloads] = useState<Download[]>([]);
    const [staticDownloads, setStaticDownloads] = useState<Array<Download>>([]);
    const [filteredDownloads, setFilteredDownloads] = useState<Array<Download>>([]);

    const [dynamicDownloads, setDynamicDownloads] = useState<DynamicDownload[]>([]);
    const [filteredDynamicDownloads, setFilteredDynamicDownloads] = useState<DynamicDownload[]>([]);
    const [inProgressDownloads, setInProgressDownloads] = useState<string[]>([]);

    const downloadService = useMemo(() => new DownloadService(activeRegion!), [activeRegion]);
    const createExport = useExportStore((state) => state.createExport);

    const { t } = useTranslation("downloads");

    const showStructuredExport = useMemo(() => {
        return isStructuredExportAvailable(versions.find((x) => x.id === activeVersion));
    }, [versions, activeVersion]);

    const selectedDisciplinesLabel = useMemo(() => {
        if (!activeVersion || !disciplines[activeVersion]) return "-";

        return (
            disciplines[activeVersion]
                .filter((x) => activeDisciplines.includes(x.id))
                .sort((a, b) => a.order - b.order)
                .map((x) => x.label?.trim())
                .filter(Boolean)
                .join(", ") || "-"
        );
    }, [disciplines, activeVersion, activeDisciplines]);

    const selectedUseCasesLabel = useMemo(() => {
        if (!activeVersion || !useCases[activeVersion]) return "-";

        let useCasesInRegion = useCases[activeVersion];
        const availableRegionCodes = regions.map((r) => r.regionCode.toLowerCase());
        if (regions.length > 0) {
            useCasesInRegion = useCasesInRegion.filter((uc) => {
                const potentialRegionCode = (uc.code || "").slice(-2).toLowerCase();
                if (availableRegionCodes.includes(potentialRegionCode)) {
                    return potentialRegionCode === activeRegion?.toLowerCase();
                }

                return true;
            });
        }

        return (
            useCasesInRegion
                .filter((x) => activeUseCases.length === 0 || activeUseCases.includes(x.id))
                .sort((a, b) => (a.code || "").localeCompare(b.code || ""))
                .map((x) => x.name?.trim())
                .filter(Boolean)
                .join(", ") || "-"
        );
    }, [useCases, activeVersion, activeUseCases, regions, activeRegion]);

    useEffect(() => {
        downloadService.get().then((downloads) => {
            // Temp filter by region, filename starting with either LU_ or BE_ need to be filtered out depending on the active region
            const downloadsSet = downloads.map((download) => {
                if (download.name.startsWith("LU_")) {
                    const activeLanguageCode = download.languageCode.split("-")[1];
                    download.name = download.name.substring(3);
                    download.languageCode = `lu-${activeLanguageCode}`;
                } else if (download.name.startsWith("BE_")) {
                    const activeLanguageCode = download.languageCode.split("-")[1];
                    download.name = download.name.substring(3);
                    download.languageCode = `be-${activeLanguageCode}`;
                } else {
                    const activeLanguageCode = download.languageCode.split("-")[1];
                    download.languageCode = `int-${activeLanguageCode}`;
                }

                if (!download.software || download.software.length === 0) {
                    download.software = ["00000000-0000-0000-0000-000000000000"];
                }

                return download;
            });

            setDownloads(
                // downloads.map((x) => ({ ...x, software: x.software ?? ["00000000-0000-0000-0000-000000000000"] })),
                downloadsSet
            );

            const staticDownloads = getStaticDownloads();
            setStaticDownloads(staticDownloads);
        });

        let versionName = versions.find((x) => x.id === activeVersion)?.name || "";
        if (versionName === "wip") versionName = versions.find((x) => x.isDefault)?.name || "";
        downloadService.getDynamic(versionName).then(setDynamicDownloads);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [downloadService, activeVersion]);

    useEffect(() => {
        // Temp filter by region, update region in language code to match the file prefix (if present) or set to 'int'.
        let filteredDownloads = [...downloads, ...staticDownloads].filter((download) => {
            if (!activeRegion) return null;

            const potentialRegionCode = download.languageCode.toLowerCase().split("-")[0];
            let containsRegionCode = false;
            if (regions && regions.length > 0) {
                containsRegionCode = regions.some(
                    (x) => x.active && x.regionCode.toLowerCase() === potentialRegionCode
                );
            }

            if (containsRegionCode && potentialRegionCode !== activeRegion.toLowerCase()) {
                return;
            }

            return true;
        });

        if (activeVersion) {
            const activeVersionName = versions.find((x) => x.id === activeVersion)?.name;
            if (activeVersionName) {
                filteredDownloads = filteredDownloads.filter((x) => x.versions.includes(activeVersionName));
            }
        }

        if (activeDisciplines.length > 0) {
            filteredDownloads = filteredDownloads.filter((x) =>
                activeDisciplines.some((y) => x.disciplines?.includes(y))
            );
        }

        if (activeIfcVersion || activeSoftware) {
            filteredDownloads = filteredDownloads.filter((x) =>
                [activeIfcVersion, activeSoftware, "00000000-0000-0000-0000-000000000000"]
                    .filter((y) => y)
                    .some((y) => x.software?.includes(y!))
            );
        }

        if (activeUseCases) {
            filteredDownloads = filteredDownloads.filter((x) =>
                activeUseCases.some(
                    (y) => x.useCases?.includes(y) || x.useCases === undefined || x.useCases.length === 0 // Include downloads with no use-cases
                )
            );
        }

        setFilteredDownloads(filteredDownloads);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [downloads, staticDownloads, activeVersion, activeDisciplines, activeSoftware, activeIfcVersion]);

    useEffect(() => {
        // Temp filter by region, update region in language code to match the file prefix (if present) or set to 'int'.
        let filteredDownloads = [...dynamicDownloads];

        if (activeDisciplines.length > 0) {
            filteredDownloads = filteredDownloads.filter((x) => activeDisciplines.includes(x.disciplineId));
        }

        if (activeIfcVersion || activeSoftware) {
            filteredDownloads = filteredDownloads.filter((x) =>
                [activeIfcVersion, activeSoftware, "00000000-0000-0000-0000-000000000000"]
                    .filter((y) => Boolean(y))
                    .some((y) => x.software === y)
            );
        }

        setFilteredDynamicDownloads(filteredDownloads);
    }, [dynamicDownloads, activeVersion, activeDisciplines, activeSoftware, activeIfcVersion]);

    const onDownload = (filename: string, location: string) => {
        saveAs(location, filename);
    };

    const onDynamicDownload = async (filename: string, file: DynamicDownload) => {
        setInProgressDownloads((prev) => [...prev, file.id]);

        const versionName = versions.find((x) => x.id === activeVersion)?.name || "";
        const blob = await downloadService.downloadDynamic(versionName, file.id, {
            language: file.language.replace("int-", activeRegion?.toLowerCase() + "-") || file.language,
            useCases: activeUseCases,
            targetSoftware: file.software === activeSoftware || file.software === idsEntry.id ? [file.software] : [],
            targetDisciplines: [file.disciplineId],
            targetIfcVersions: file.ifcVersions.filter((x) => x === activeIfcVersion) || [],
        });

        saveAs(blob, filename);
        setInProgressDownloads((prev) => prev.filter((x) => x !== file.id));
    };

    return (
        <div className="space-y-4">
            <div className="grid bg-primary-200 rounded-xl p-5 border border-primary-300">
                <div className="grid grid-cols-[auto_180px] m-5">
                    <div className="flex flex-col">
                        <strong className="text-primary-500">{t("contract-export.title", { ns: "downloads" })}</strong>
                        <span className="text-sm text-zinc-600">
                            {t("contract-export.description", { ns: "downloads" })}
                        </span>
                    </div>
                    <button
                        className="rounded-md bg-primary-500 text-white gap-2 ml-5 inline-flex items-center justify-center font-medium px-4 py-2 shrink-0 cursor-pointer hover:bg-primary-600 hover:text-white text-base"
                        onClick={() => createExport("website-export")}
                    >
                        {t("download-button", { ns: "downloads" })}
                    </button>
                    <hr className="border-t border-primary-400 my-5 col-span-2" />
                    <div className="col-span-2">
                        <span className="text-sm font-medium">{t("disciplines", { ns: "downloads" })}: </span>
                        <span className="text-sm text-zinc-600">{selectedDisciplinesLabel}</span>
                    </div>
                    <div className="col-span-2">
                        <span className="text-sm font-medium">{t("use-cases", { ns: "downloads" })}: </span>
                        <span className="text-sm text-zinc-600">{selectedUseCasesLabel}</span>
                    </div>
                </div>
            </div>

            {showStructuredExport && (
                <div className="grid bg-primary-200 rounded-xl p-5 border border-primary-300">
                    <div className="grid grid-cols-[auto_180px] m-5">
                        <div className="flex flex-col">
                            <strong className="text-primary-500">
                                {t("daily-management-export.title", { ns: "downloads" })}
                            </strong>
                            <span className="text-sm text-zinc-600">
                                {t("daily-management-export.description", { ns: "downloads" })}
                            </span>
                        </div>
                        <button
                            className="rounded-md bg-primary-500 text-white gap-2 ml-5 inline-flex items-center justify-center font-medium px-4 py-2 shrink-0 cursor-pointer hover:bg-primary-600 hover:text-white text-base"
                            onClick={() => createExport("structured-export")}
                        >
                            {t("download-button", { ns: "downloads" })}
                        </button>
                        <hr className="border-t border-primary-400 my-5 col-span-2" />
                        <div className="col-span-2">
                            <span className="text-sm font-medium">{t("disciplines", { ns: "downloads" })}: </span>
                            <span className="text-sm text-zinc-600">{selectedDisciplinesLabel}</span>
                        </div>
                        <div className="col-span-2">
                            <span className="text-sm font-medium">{t("use-cases", { ns: "downloads" })}: </span>
                            <span className="text-sm text-zinc-600">{selectedUseCasesLabel}</span>
                        </div>
                    </div>
                </div>
            )}

            {/* {filteredDownloads.length === 0 && filteredDynamicDownloads.length === 0 && (
                <div className="relative w-full col-start-2 mb-40">
                    <h2 className="leading-16">{t("list-empty.title", { ns: "downloads" })}</h2>
                    <p className="font-medium text-base/normal">{t("list-empty.description", { ns: "downloads" })}</p>
                </div>
            )} */}

            {orderBy(filteredDynamicDownloads, ["bidsVersion", "name"], ["desc", "asc"]).map((file) => {
                const extension = `.zip`;
                let filename = file.name.trim();
                if (!filename.toLocaleLowerCase().endsWith(extension)) filename = `${filename}${extension}`;

                const downloadInProgress = inProgressDownloads.some((x) => x === file.id);
                const versionId = versions.find((x) => x.name === file.bidsVersion)?.id || "";
                const attachedDiscipline = disciplines[versionId].find((x) => x.id === file.disciplineId) || {
                    label: "-",
                };

                let useCasesInRegion = useCases[activeVersion!]?.filter((uc) => {
                    if (uc.attachedDiscipline === undefined || uc.attachedDiscipline === null) return true;

                    return uc.attachedDiscipline.includes(file.disciplineId);
                });

                const availableRegionCodes = regions.map((r) => r.regionCode.toLowerCase());
                if (regions.length > 0) {
                    useCasesInRegion = useCasesInRegion?.filter((uc) => {
                        const potentialRegionCode = uc.code.slice(-2).toLowerCase();
                        if (availableRegionCodes.includes(potentialRegionCode)) {
                            return potentialRegionCode === activeRegion?.toLowerCase();
                        }

                        return true;
                    });
                }

                const attachedUseCases =
                    useCasesInRegion
                        ?.filter((x) => file.useCases?.includes(x.id))
                        .filter((x) => activeUseCases.length === 0 || activeUseCases.some((y) => y === x.id))
                        .sort((a, b) => a.code.localeCompare(b.code))
                        .map((x) => x.name.trim())
                        .join(", ") || "-";

                const attachedSoftware =
                    [...software[versionId], idsEntry]
                        ?.filter((x) => file.software?.includes(x.id) && activeSoftware === x.id)
                        .filter((x) => x.active && !x.isIfc)
                        .sort((a, b) => a.order - b.order)
                        .map((x) => x.name.trim())
                        .join(", ") || "-";

                const attachedIfc =
                    [...software[versionId]]
                        ?.filter((x) => file.ifcVersions?.includes(x.id) && activeIfcVersion === x.id)
                        .filter((x) => x.active && x.isIfc)
                        .sort((a, b) => a.order - b.order)
                        .map((x) => x.name.trim())
                        .join(", ") || "-";

                return (
                    <div key={file.id} className="grid bg-primary-100 rounded-xl p-5 border border-primary-200">
                        <div className="grid grid-cols-[auto_100px_180px] m-5">
                            <div className="flex flex-col">
                                <strong
                                    className={clsx([
                                        "text-primary-500",
                                        downloadInProgress && "opacity-50 pointer-events-none",
                                    ])}
                                >
                                    <Link to={""} onClick={() => onDynamicDownload(filename, file)}>
                                        {filename}
                                    </Link>
                                </strong>
                                <span className="text-sm">
                                    {t("version", { ns: "downloads" })}: {file.bidsVersion}
                                </span>
                            </div>
                            <div className="flex flex-col">
                                <span className="text-sm font-medium">{t("upload-date", { ns: "downloads" })}</span>
                                <span className="text-sm text-zinc-500">
                                    {parseJSON(file.uploadDate).toLocaleDateString("fr-FR")}
                                </span>
                            </div>
                            <button
                                className={clsx(
                                    "text-white py-2 px-4 rounded-md ml-5 cursor-pointer hover:bg-primary-600 hover:text-white text-base font-medium",
                                    !downloadInProgress ? "bg-primary-500" : "pointer-events-none bg-zinc-300"
                                )}
                                onClick={() => onDynamicDownload(filename, file)}
                            >
                                {downloadInProgress ? (
                                    <div role="status">
                                        <svg
                                            aria-hidden="true"
                                            className="inline w-6 h-6 text-neutral-tertiary animate-spin fill-primary-500"
                                            viewBox="0 0 100 101"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                                                fill="currentColor"
                                            />
                                            <path
                                                d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                                                fill="currentFill"
                                            />
                                        </svg>
                                        <span className="sr-only">Loading...</span>
                                    </div>
                                ) : (
                                    t("download-button", { ns: "downloads" })
                                )}
                            </button>
                            <hr className="border-t border-primary-300 my-5 col-span-3" />
                            <div className="col-span-3">
                                <span className="text-sm font-medium">{t("disciplines", { ns: "downloads" })}: </span>
                                <span className="text-sm text-zinc-500">{attachedDiscipline.label}</span>
                            </div>
                            <div className="col-span-3">
                                <span className="text-sm font-medium">{t("use-cases", { ns: "downloads" })}: </span>
                                <span className="text-sm text-zinc-500">{attachedUseCases}</span>
                            </div>
                            <div className="col-span-3">
                                <span className="text-sm font-medium">{t("software", { ns: "downloads" })}: </span>
                                <span className="text-sm text-zinc-500">{attachedSoftware}</span>
                            </div>
                            <div className="col-span-3">
                                <span className="text-sm font-medium">{t("ifc-version", { ns: "downloads" })}: </span>
                                <span className="text-sm text-zinc-500">{attachedIfc}</span>
                            </div>
                        </div>
                    </div>
                );
            })}

            {orderBy(filteredDownloads, ["version", "language", "name"], ["desc", "asc", "asc"]).map((file) => {
                const extension = `.${file.extension}`;
                let filename = file.name.trim();
                if (!filename.toLocaleLowerCase().endsWith(extension)) filename = `${filename}${extension}`;

                const versionId = versions.find((x) => x.name === file.versions[0])?.id || "";
                const attachedDisciplines = disciplines[versionId]
                    ?.filter((x) => file.disciplines?.includes(x.id))
                    .sort((a, b) => a.order - b.order)
                    .map((x) => x.label)
                    .join(", ");

                let useCasesInRegion = useCases[activeVersion!];
                const availableRegionCodes = regions.map((r) => r.regionCode.toLowerCase());
                if (regions.length > 0) {
                    useCasesInRegion = useCasesInRegion.filter((uc) => {
                        const potentialRegionCode = uc.code.slice(-2).toLowerCase();
                        if (availableRegionCodes.includes(potentialRegionCode)) {
                            return potentialRegionCode === activeRegion?.toLowerCase();
                        }

                        return true;
                    });
                }

                const attachedUseCases =
                    useCasesInRegion
                        ?.filter((x) => file.useCases?.includes(x.id))
                        .filter((x) => activeUseCases.length === 0 || activeUseCases.some((y) => y === x.id))
                        .sort((a, b) => a.code.localeCompare(b.code))
                        .map((x) => x.name.trim())
                        .join(", ") || "-";

                // const attachedSoftware = [...software[versionId], idsEntry]
                //     ?.filter((x) => file.software?.includes(x.id))
                //     .sort((a, b) => a.order - b.order)
                //     .map((x) => x.name);

                return (
                    <div key={file.id} className="grid bg-primary-100 rounded-xl p-5 border border-primary-200">
                        <div className="grid grid-cols-[auto_100px_100px_180px] m-5">
                            <div className="flex flex-col">
                                <strong className="text-primary-500">
                                    <Link to={""} onClick={() => onDownload(filename, file.location)}>
                                        {filename}
                                    </Link>
                                </strong>
                                <span className="text-sm">
                                    {t("version", { ns: "downloads" })}: {file.versions.join(", ")}
                                </span>
                            </div>
                            <div className="flex flex-col">
                                {/* <span className="text-sm font-medium">{t("file-size", { ns: "downloads" })}</span>
                                <span className="text-sm text-zinc-500">{getBytes(file.size)}</span> */}
                            </div>
                            <div className="flex flex-col">
                                <span className="text-sm font-medium">{t("upload-date", { ns: "downloads" })}</span>
                                <span className="text-sm text-zinc-500">
                                    {parseJSON(file.uploadDate).toLocaleDateString("fr-FR")}
                                </span>
                            </div>
                            <button
                                className="bg-primary-500 text-white py-2 px-4 rounded-md ml-5 cursor-pointer hover:bg-primary-600 hover:text-white text-base font-medium"
                                onClick={() => onDownload(filename, file.location)}
                            >
                                {t("download-button", { ns: "downloads" })}
                            </button>
                            <hr className="border-t border-primary-300 my-5 col-span-4" />
                            <div className="col-span-4">
                                <span className="text-sm font-medium">{t("disciplines", { ns: "downloads" })}: </span>
                                <span className="text-sm text-zinc-500">{attachedDisciplines}</span>
                            </div>
                            <div className="col-span-4">
                                <span className="text-sm font-medium">{t("use-cases", { ns: "downloads" })}: </span>
                                <span className="text-sm text-zinc-500">{attachedUseCases}</span>
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default Downloads;
